import React, { useState, useContext, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { StoreContext } from '../../contexts/StoreContext';
import PreviewModal from '../CalculationPreview/PreviewModal';
import styles from './TemplateSelector.module.css';

/**
 * Компонент для выбора шаблона при создании расчета
 * @param {string} templateType - Тип шаблона (works/documents/support)
 * @param {function} onSelectTemplate - Callback при выборе шаблона
 */
const TemplateSelector = observer(({ templateType, onSelectTemplate }) => {
    const rootStore = useContext(StoreContext);
    const { models } = rootStore;

    const [searchQuery, setSearchQuery] = useState('');
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);

    // Получаем коллекцию шаблонов
    const templatesStore = models.getCollectionManager('templates');

    // Загружаем шаблоны при монтировании если коллекция пуста
    useEffect(() => {
        if (!templatesStore) return;

        // Загружаем если пусто
        if (templatesStore.items?.length === 0 && !templatesStore.isLoading) {
            templatesStore.load();
        }
    }, [templatesStore]);

    // Фильтрация и поиск шаблонов
    // observer() обеспечивает реактивность - useMemo не нужен
    const getFilteredTemplates = () => {
        if (!templatesStore?.items) return [];

        let filtered = templatesStore.items.slice(); // slice для создания копии

        // Фильтр по типу
        if (templateType) {
            filtered = filtered.filter(t => t.type === templateType);
        }

        // Полнотекстовый поиск по названию и тегам
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(template => {
                const nameMatch = template.templateName?.toLowerCase().includes(query);
                const tagsMatch = template.tags?.some(tag => tag.toLowerCase().includes(query));
                return nameMatch || tagsMatch;
            });
        }

        return filtered;
    };

    const filteredTemplates = getFilteredTemplates();

    // Обработчик выбора шаблона
    const handleSelect = (template) => {
        setSelectedTemplateId(template.id);
        if (onSelectTemplate) {
            onSelectTemplate(template);
        }
    };

    // Обработчик предпросмотра
    const handlePreview = (template) => {
        setPreviewTemplate(template);
    };

    // Закрытие предпросмотра
    const handleClosePreview = () => {
        setPreviewTemplate(null);
    };

    return (
        <div className={styles.container}>
            {/* Поиск */}
            <div className={styles.searchBox}>
                <input
                    type="text"
                    className={styles.searchInput}
                    placeholder="Поиск по названию или тегам..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            {/* Список шаблонов */}
            <div className={styles.templatesList}>
                {filteredTemplates.length === 0 ? (
                    <div className={styles.emptyState}>
                        {searchQuery ? 'Шаблоны не найдены' : 'Нет шаблонов для данного типа'}
                    </div>
                ) : (
                    filteredTemplates.map(template => (
                        <div
                            key={template.id}
                            className={`${styles.templateCard} ${selectedTemplateId === template.id ? styles.selected : ''}`}
                            onClick={() => handleSelect(template)}
                        >
                            {/* Название */}
                            <div className={styles.templateName}>
                                {template.templateName}
                            </div>

                            {/* Теги */}
                            {template.tags && template.tags.length > 0 && (
                                <div className={styles.tagsContainer}>
                                    {template.tags.map((tag, index) => (
                                        <span key={index} className={styles.tag}>
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Иконка предпросмотра */}
                            <button
                                type="button"
                                className={styles.previewIcon}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePreview(template);
                                }}
                                title="Предпросмотр"
                                aria-label="Предпросмотр шаблона"
                            >
                                👁️
                            </button>
                        </div>
                    ))
                )}
            </div>

            {/* Модалка предпросмотра */}
            {previewTemplate && (
                <PreviewModal
                    isOpen={true}
                    data={previewTemplate}
                    isTemplate={true}
                    onClose={handleClosePreview}
                />
            )}
        </div>
    );
});

export default TemplateSelector;
